import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth';
import { api } from '../../lib/api';
import {
  HomeIcon,
  UsersIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftIcon,
  UserGroupIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

function ParkScene() {
  return (
    <svg viewBox="0 0 256 180" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="xMidYMax meet">
      <defs>
        <linearGradient id="fadeUp" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3D5A5F" stopOpacity="1" />
          <stop offset="35%" stopColor="#3D5A5F" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3D5A5F" />
          <stop offset="100%" stopColor="#5A7B6F" />
        </linearGradient>
      </defs>
      <rect width="256" height="180" fill="url(#skyGrad)" />
      <circle cx="190" cy="50" r="35" fill="#C4754C" opacity="0.15" />
      <circle cx="190" cy="50" r="18" fill="#C4754C" opacity="0.25" />
      <circle cx="190" cy="50" r="10" fill="#F5F0E8" opacity="0.5" />
      <path d="M0 120 L40 65 L65 85 L95 50 L125 80 L150 55 L180 75 L210 45 L240 70 L256 60 L256 180 L0 180Z" fill="#2C3E3D" opacity="0.4" />
      <path d="M0 140 L30 95 L55 110 L80 80 L110 100 L140 75 L170 105 L200 85 L230 100 L256 90 L256 180 L0 180Z" fill="#2C3E3D" opacity="0.55" />
      <path d="M80 80 L85 87 L75 87Z" fill="#F5F0E8" opacity="0.3" />
      <path d="M140 75 L146 84 L134 84Z" fill="#F5F0E8" opacity="0.3" />
      <path d="M200 85 L205 92 L195 92Z" fill="#F5F0E8" opacity="0.3" />
      <path d="M0 155 L20 140 L50 148 L80 135 L110 145 L140 130 L170 142 L200 132 L230 145 L256 138 L256 180 L0 180Z" fill="#5A7B6F" opacity="0.7" />
      <path d="M25 155 L30 138 L35 155Z" fill="#42574B" />
      <path d="M30 155 L35 132 L40 155Z" fill="#3C4E42" />
      <path d="M20 155 L25 140 L30 155Z" fill="#486054" />
      <path d="M95 150 L100 128 L105 150Z" fill="#42574B" />
      <path d="M100 150 L105 125 L110 150Z" fill="#3C4E42" />
      <path d="M108 150 L113 130 L118 150Z" fill="#486054" />
      <path d="M175 148 L180 126 L185 148Z" fill="#42574B" />
      <path d="M182 148 L187 122 L192 148Z" fill="#3C4E42" />
      <path d="M190 148 L195 130 L200 148Z" fill="#486054" />
      <path d="M0 160 Q64 152, 128 158 Q192 164, 256 156 L256 180 L0 180Z" fill="#5A7B6F" opacity="0.85" />
      <path d="M100 180 Q110 170, 120 165 Q135 158, 145 162 Q160 168, 170 180" fill="#C4754C" opacity="0.25" />
      <path d="M60 40 Q65 35, 70 40" stroke="#F5F0E8" strokeWidth="1" fill="none" opacity="0.3" />
      <path d="M75 35 Q80 30, 85 35" stroke="#F5F0E8" strokeWidth="1" fill="none" opacity="0.25" />
      <circle cx="40" cy="25" r="1" fill="#F5F0E8" opacity="0.3" />
      <circle cx="120" cy="18" r="1" fill="#F5F0E8" opacity="0.25" />
      <rect width="256" height="180" fill="url(#fadeUp)" />
    </svg>
  );
}

function SidebarContent({ nav, isActive, handleLogout, onNavClick }: {
  nav: { name: string; href: string; icon: any; badge?: number }[];
  isActive: (href: string) => boolean;
  handleLogout: () => void;
  onNavClick?: () => void;
}) {
  return (
    <>
      <div className="p-5 border-b border-teal-600/30">
        <Link to="/admin" className="flex items-center gap-3" onClick={onNavClick}>
          <img src="/logo-sidebar.png" alt="Trailhead" className="h-10 w-10 rounded-lg shadow-copper-sm" />
          <div>
            <h1 className="font-bold text-lg text-cream-50">Trailhead</h1>
            <p className="text-xs text-cream-300/70">Admin Panel</p>
          </div>
        </Link>
      </div>

      <nav className="flex-1 p-4 space-y-1 relative z-10">
        {nav.map((item) => (
          <Link
            key={item.name}
            to={item.href}
            onClick={onNavClick}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive(item.href)
                ? 'bg-copper-500/90 text-white shadow-copper-sm'
                : 'text-cream-100 hover:bg-teal-400/20 hover:text-white'
            }`}
          >
            <item.icon className="h-5 w-5" />
            {item.name}
            {item.badge ? (
              <span className="ml-auto bg-copper-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {item.badge}
              </span>
            ) : null}
          </Link>
        ))}
      </nav>

      <div className="p-4 border-t border-teal-600/30 space-y-2 relative z-10">
        <Link
          to="/"
          onClick={onNavClick}
          className="flex items-center gap-2 px-3 py-2 text-sm text-cream-200/80 hover:text-white transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to My Account
        </Link>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-3 py-2 text-sm text-cream-300/60 hover:text-white transition-colors w-full"
        >
          <ArrowRightOnRectangleIcon className="h-4 w-4" />
          Logout
        </button>
      </div>

      <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
        <ParkScene />
      </div>
    </>
  );
}

export default function AdminLayout() {
  const { logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const data = await api.getConversations();
        setUnreadCount(data.total_unread);
      } catch (e) { /* ignore */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const nav = [
    { name: 'Dashboard', href: '/admin', icon: HomeIcon },
    { name: 'Clients', href: '/admin/clients', icon: UsersIcon },
    { name: 'Coach Inbox', href: '/admin/coach', icon: ChatBubbleLeftRightIcon, badge: unreadCount },
    { name: 'Team', href: '/admin/team', icon: UserGroupIcon },
  ];

  const isActive = (href: string) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center gap-3 px-4 py-3 shadow-md" style={{ backgroundColor: '#3D5A5F' }}>
        <button
          onClick={() => setSidebarOpen(true)}
          className="text-cream-50 p-1 -ml-1"
          aria-label="Open menu"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
        <div className="flex items-center gap-2">
          <img src="/logo-sidebar.png" alt="Trailhead" className="h-7 w-7 rounded-lg" />
          <h1 className="font-bold text-cream-50">Trailhead</h1>
        </div>
        {unreadCount > 0 && (
          <Link to="/admin/coach" className="ml-auto">
            <span className="bg-copper-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {unreadCount}
            </span>
          </Link>
        )}
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setSidebarOpen(false)}
          />
          {/* Drawer */}
          <aside
            className="relative w-72 max-w-[80vw] flex flex-col flex-shrink-0 overflow-hidden"
            style={{ backgroundColor: '#3D5A5F' }}
          >
            <button
              onClick={() => setSidebarOpen(false)}
              className="absolute top-4 right-4 z-20 text-cream-100 hover:text-white"
              aria-label="Close menu"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <SidebarContent
              nav={nav}
              isActive={isActive}
              handleLogout={handleLogout}
              onNavClick={() => setSidebarOpen(false)}
            />
          </aside>
        </div>
      )}

      {/* Desktop sidebar - hidden on mobile */}
      <aside className="hidden lg:flex w-64 flex-col flex-shrink-0 relative overflow-hidden" style={{ backgroundColor: '#3D5A5F' }}>
        <SidebarContent
          nav={nav}
          isActive={isActive}
          handleLogout={handleLogout}
        />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 p-4 lg:p-6 overflow-auto pt-16 lg:pt-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
